/**
 * Insforge Design System Theme
 *
 * Complete design tokens for all components
 */

export const theme = {
  colors: {
    primary: '#000000',
    primaryHover: '#1f1f1f',
    text: '#000000',
    textSecondary: '#828282',
    textMuted: '#a3a3a3',
    textGray: '#525252',
    border: '#d4d4d4',
    borderGray: '#e4e4e7',
    borderHover: '#9ca3af',
    borderFocus: '#000000',
    bgWhite: '#ffffff',
    bgLight: '#fafafa',
    bgGray: '#f5f5f5',
    bgHover: '#f9fafb',
    error: '#dc2626',
    errorBg: '#fee2e2',
    success: '#16a34a',
    successBg: '#d1fae5',
    successDark: '#059669',
    black: '#000000',
    zinc900: '#09090b',
  },

  spacing: {
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
  },

  radius: {
    xs: '0.125rem', // 2px
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    full: '9999px',
  },

  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
  },

  lineHeight: {
    tight: '1rem', // 16px
    normal: '1.25rem', // 20px
    relaxed: '1.5rem', // 24px
    loose: '2rem', // 32px
  },

  fontFamily: {
    base: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    manrope: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
  },

  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },

  transition: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  sizes: {
    input: {
      height: '2.5rem', // 40px
    },
    button: {
      height: '2.5rem', // 40px for submit
      heightOAuth: '2.25rem', // 36px for OAuth
    },
    avatar: '2rem', // 32px
    icon: '1.25rem', // 20px
    iconSm: '1.125rem', // 18px
    iconLg: '1.5rem', // 24px
    verifyCode: '3rem', // 48px
    verifyIcon: '4rem', // 64px
    verifyIconInner: '2rem', // 32px
  },
} as const;

export type Theme = typeof theme;

/**
 * Keyframe animations
 */
export const keyframes = {
  spin: `
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  `,
};

/**
 * Google Fonts imports
 */
export const fontImports = [
  'https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap',
  'https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap',
];
